// apps/dashboard/src/components/OnboardingChecklist.tsx
// Compact onboarding checklist widget rendered inside the sidebar.
// Visible only when onboarding is incomplete and not dismissed.
// SPEC: docs/specs/tenant/sprint-al-onboarding.spec.md

import { useOnboardingDismiss, useOnboardingStatus } from '@/hooks/use-billing';
import { cn } from '@/lib/cn';
import { Link } from '@tanstack/react-router';
import { X } from 'lucide-react';
import { OnboardingStep } from './OnboardingStep';

const CHECKLIST_STEPS = [
  { id: 'CHANNEL', label: 'Conectar canal' },
  { id: 'INBOX', label: 'Criar inbox' },
  { id: 'AGENT', label: 'Convidar agente' },
  { id: 'IMPORT_CONTACTS', label: 'Importar contatos' },
  { id: 'TEST_MESSAGE', label: 'Enviar mensagem' },
] as const;

interface OnboardingChecklistProps {
  collapsed?: boolean;
}

export function OnboardingChecklist({ collapsed = false }: OnboardingChecklistProps) {
  const { data, isLoading } = useOnboardingStatus();
  const dismiss = useOnboardingDismiss();

  const onboarding = data?.onboarding;

  // Don't render when collapsed, loading, done, or dismissed
  if (
    collapsed ||
    isLoading ||
    !onboarding ||
    onboarding.currentStep === 'DONE' ||
    onboarding.isDismissed
  ) {
    return null;
  }

  const completedSet = new Set(onboarding.completedSteps);
  const totalSteps = CHECKLIST_STEPS.length;
  const completedCount = CHECKLIST_STEPS.filter((s) => completedSet.has(s.id)).length;
  const pct = Math.round((completedCount / totalSteps) * 100);

  return (
    <div className="mx-2 mb-2 rounded-lg border border-border bg-muted/40 p-3">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-foreground">Configuração inicial</span>
        <button
          type="button"
          onClick={() => dismiss.mutate()}
          disabled={dismiss.isPending}
          className={cn(
            'rounded p-0.5 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors',
          )}
          aria-label="Fechar checklist"
        >
          <X className="h-3 w-3" />
        </button>
      </div>

      {/* Progress bar */}
      <div className="mb-2.5">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] text-muted-foreground">
            {completedCount}/{totalSteps} concluídos
          </span>
          <span className="text-[10px] text-muted-foreground">{pct}%</span>
        </div>
        <div className="h-1 rounded-full bg-muted overflow-hidden">
          <div
            className="h-full bg-primary rounded-full transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Steps */}
      <div className="space-y-0.5">
        {CHECKLIST_STEPS.map((step) => (
          <OnboardingStep
            key={step.id}
            label={step.label}
            isCompleted={completedSet.has(step.id)}
            isCurrent={onboarding.currentStep === step.id}
          />
        ))}
      </div>

      {/* Link to full wizard */}
      <Link
        to="/onboarding"
        className="mt-2.5 flex items-center text-[10px] font-medium text-primary hover:underline"
      >
        Ver detalhes →
      </Link>
    </div>
  );
}
